import { useQuery } from "@tanstack/react-query";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import { extractApiError } from "~/shared/utils/extract-api-error.ts";

import type {
	GridPreference,
	UserPreferences,
} from "../../schemas/user-preferences-schema.ts";
import { userPreferencesSchema } from "../../schemas/user-preferences-schema.ts";

export const INTERNAL_USER_PREFERENCES_QUERY_KEY = [
	"internal-user-preferences",
] as const;

/**
 * Builds the exact query key the internal user-preferences query is cached
 * under. Any code that writes to this cache with `setQueryData` must use this
 * exact key — `setQueryData` matches by exact key.
 */
export const getInternalUserPreferencesQueryKey = () =>
	INTERNAL_USER_PREFERENCES_QUERY_KEY;

const getInternalUserPreferences = async (
	url: string,
): Promise<UserPreferences> => {
	const response = await getAndValidate(url, userPreferencesSchema);
	if (!response.success) {
		const apiError = await extractApiError(response.error);
		throw new Error(
			apiError?.error ?? "Failed to fetch internal user preferences",
		);
	}

	return response.data as UserPreferences;
};

type UseGetInternalUserPreferencesResult<T> = {
	data: T | undefined;
	isLoading: boolean;
	error: Error | null;
};

export function useGetInternalUserPreferences(): UseGetInternalUserPreferencesResult<UserPreferences>;
export function useGetInternalUserPreferences(
	gridId: number,
): UseGetInternalUserPreferencesResult<GridPreference>;
export function useGetInternalUserPreferences(
	gridId?: number,
): UseGetInternalUserPreferencesResult<UserPreferences | GridPreference> {
	const { isInternal } = useDeploymentType();

	const query = useQuery({
		queryKey: getInternalUserPreferencesQueryKey(),
		queryFn: () =>
			getInternalUserPreferences(apiRoutes.INTERNALUSERPREFERENCES),
		retry: false,
		// Mirror the external hook's mount behavior: once the query has an
		// answer (data or error), don't refetch or retry when a new subscriber
		// mounts, to avoid an errored query flipping back to `pending` and
		// producing a remount/refetch flicker loop.
		refetchOnMount: false,
		retryOnMount: false,
		// This is the internal-only counterpart to the external/vendor
		// preference fetch. External/vendor users read their own dedicated
		// endpoints, so only run this request for internal users.
		enabled: isInternal,
	});

	return {
		data: gridId === undefined ? query.data : query.data?.[String(gridId)],
		isLoading: query.isLoading,
		error: query.error,
	};
}
